// The entrance's frame (app/art/welcome/PLAN.md section 4.0): /welcome,
// /locked, /create and /restore are the next room of the home page, not the
// app console. The site header with the network pill in Explore Vault's
// slot, the development banner and the advisory slot under it, one column
// on the page's ground, and a footer of the ways out. The skip link and
// main#main keep App.tsx's focus on route change.
// - width: 'door' (600 px, centred: Welcome, Locked), 'read' (600 px, left:
//   Create, Restore) or 'wide' (760 px: the phrase, confirm and words steps);
// - art: the still stage (EntranceArt), above the column and wider than it;
// - secret: words or a passphrase exist on this step, so the header's links
//   go and the wordmark is plain text (SiteHeader), and the footer keeps
//   only Docs, which opens a new tab: a same-tab link would unmount the
//   step and end the worker that holds the words, with no prompt;
// - busy: the session has not answered yet; the column is reserved and empty.

import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { useSession } from '../session';
import { SiteHeader } from '../site/chrome';
import { ADVISORY } from '../site/facts';
import '../site/site.css';
import '../design/entrance.css';

export function EntranceFrame({
  width = 'door',
  art,
  secret,
  busy,
  children,
}: {
  width?: 'door' | 'read' | 'wide';
  art?: ReactNode;
  secret?: boolean;
  busy?: boolean;
  children?: ReactNode;
}) {
  const { development } = useSession();
  return (
    <div className="site entrance">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <SiteHeader variant="entrance" secret={secret} />
      {development ? (
        <div className="dev-banner" role="status">
          DEVELOPMENT SESSION · the program check is off until you lock
        </div>
      ) : null}
      {ADVISORY ? (
        <p className="advisory" role="note">
          <b>Advisory</b>
          {ADVISORY}
        </p>
      ) : null}
      <main id="main" className="entry" tabIndex={-1}>
        {art}
        <div className={`entry-col col-${width}${busy ? ' reserve' : ''}`} aria-busy={busy ? 'true' : undefined}>
          {children}
        </div>
      </main>
      <footer className="efoot">
        <nav aria-label="Footer">
          {secret ? null : (
            <>
              <Link href="/protection">How protection works</Link>
              <Link href="/verify">Verify the deployment</Link>
            </>
          )}
        </nav>
      </footer>
    </div>
  );
}

/** The one status line on an entrance screen, directly under its primary action (home's gold dot). */
export function EntranceStatus({ link = true }: { link?: boolean }) {
  return (
    <p className="status">
      <i aria-hidden="true" />
      <span>Experimental · Unaudited</span>
      {link ? (
        <>
          <span className="sep" aria-hidden="true">
            ·
          </span>
          <Link href="/protection">How protection works</Link>
        </>
      ) : null}
    </p>
  );
}
